import { FC, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Modal } from '@crisellevaldez/speculo-ui';
import { ContactHeader } from '../calls/ContactHeader';
import { ArrowDownLeft, ArrowUpRight, MoreHorizontal } from 'lucide-react';
import {
  useEmailThreadGQL,
  type EmailThreadKinds,
  type EmailThreadMessage,
} from '../../../hooks/graphql/contacts/useEmailThreadGQL';
import { useNewItemsHighlight } from '../../../hooks/useNewItemsHighlight';
import { useThreadEdgePaging } from '../../../hooks/useThreadEdgePaging';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { ThreadPagerPill } from '../../common/ThreadPagerPill';
import { formatDate } from '../../../utils/dateTime';
import { emailSrcDoc } from '../../../utils/email/emailSrcDoc';
import { emailLeadAddress } from '../../../utils/email/emailLeadAddress';
import { orgEmailSender } from '../../../utils/email/orgEmailSender';
import { linkify } from '../../../utils/linkify';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  contactId: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  /** Thread to open; optional when `anchorMessageId` is given, since a message knows its thread. */
  conversationId?: string | null;
  /** Message to open the thread at (expanded and scrolled to). Without it the thread opens at the latest email. */
  anchorMessageId?: string | null;
  /** Makes the contact name a link, e.g. to open the contact modal over the thread. */
  onContactClick?: (contactId: string) => void;
  /** The conversation's email count from the row that opened it (`threadMessageCount`). */
  totalCount?: number | null;
  /** Which emails to list; Remi conversation emails unless given. Null lists every kind. */
  emailKinds?: EmailThreadKinds;
}

interface Party {
  name: string;
  email: string;
}

const Avatar: FC<{ name: string; inbound: boolean }> = ({ name, inbound }) => (
  <span
    className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-[12px] font-semibold flex-shrink-0 ${
      inbound
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
        : 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
    }`}
    aria-hidden
  >
    {(name.trim()[0] || '?').toUpperCase()}
  </span>
);

/** A sent email rendered as it was sent, sized to its content. */
const EmailHtmlFrame: FC<{ html: string; title: string }> = ({ html, title }) => {
  const srcDoc = useMemo(() => emailSrcDoc(html, null, '0', '15px'), [html]);
  const [height, setHeight] = useState(120);
  return (
    <iframe
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-same-origin"
      className="w-full border-0 rounded-md bg-white"
      style={{ height }}
      onLoad={(e) => {
        const doc = e.currentTarget.contentDocument;
        if (doc) setHeight(doc.documentElement.scrollHeight);
      }}
    />
  );
};

const EmailCard: FC<{
  email: EmailThreadMessage;
  from: Party;
  to: Party;
  expanded: boolean;
  highlighted: boolean;
  /** Just loaded with an older or newer page; glows briefly. */
  fresh: boolean;
  timezone: string;
  onToggle: () => void;
}> = ({ email, from, to, expanded, highlighted, fresh, timezone, onToggle }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  const inbound = (email.direction || '').toLowerCase() === 'inbound';
  const visible = email.replyText ?? email.body ?? '';
  const quoted = email.quotedText ?? '';
  const sentAt = email.sentAt || email.createdAt;
  const snippet = visible.replace(/\s+/g, ' ');

  return (
    <div
      data-msg-id={email.messageId}
      className={`px-3 border-b border-foreground/10 last:border-b-0 ${
        highlighted ? 'bg-primaryfortext/[0.04] rounded-lg' : ''
      } ${fresh ? 'animate-thread-loaded-row rounded-lg' : ''}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full text-left flex items-start gap-3 pt-4 pb-3 hover:bg-muted/30 transition-colors"
      >
        <Avatar name={from.name} inbound={inbound} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0 truncate text-[14px]">
              <span className="font-semibold text-foreground">{from.name}</span>
              {expanded && from.email && (
                <span className="text-muted-foreground"> &lt;{from.email}&gt;</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[12px] text-muted-foreground tabular-nums whitespace-nowrap">
                {sentAt && formatDate(sentAt, timezone, 'MMM D, h:mm A')}
              </span>
            </div>
          </div>
          {expanded ? (
            <div className="text-[12.5px] text-muted-foreground truncate mt-1">
              to {to.name}
              {to.email && to.email !== to.name ? ` <${to.email}>` : ''}
            </div>
          ) : (
            // Same preview line as the SMS list: direction arrow, one line cut with an ellipsis.
            <div className="flex items-start gap-1.5 sm:gap-2 mt-2 text-[13.5px] text-foreground/90 min-w-0">
              {inbound ? (
                <ArrowDownLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-inbound flex-shrink-0 mt-0.5" />
              ) : (
                <ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-outbound flex-shrink-0 mt-0.5" />
              )}
              <span className="block min-w-0 max-w-[1000px] truncate" title={snippet}>
                {snippet || '(no text)'}
              </span>
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="pl-11 pb-5">
          {/* Remi's emails show as sent (formatting, property cards); a lead's reply
              shows its text with the quoted history behind the toggle below. */}
          {!inbound && email.bodyHtml ? (
            <EmailHtmlFrame html={email.bodyHtml} title={email.subject || 'Email'} />
          ) : (
            <div className="text-[15px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {visible ? (
                linkify(visible)
              ) : (
                <span className="italic text-muted-foreground">(no text)</span>
              )}
            </div>
          )}
          {quoted && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowQuoted((v) => !v)}
                className="inline-flex items-center justify-center h-4 px-1.5 rounded bg-muted hover:bg-muted/70 text-muted-foreground"
                title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
                aria-label={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
              {showQuoted && (
                <div className="mt-2 pl-3 border-l-2 border-foreground/15 text-[14px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
                  {quoted}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type ViewProps = Omit<Props, 'isOpen' | 'onClose'> & {
  /** Whether the thread is on screen; its queries run only while it is. */
  active: boolean;
  /** Places the header and the thread body, e.g. into a modal's header and body. */
  children: (header: ReactNode, body: ReactNode) => ReactNode;
};

/**
 * An email conversation laid out like an email client: one card per email,
 * oldest first, the opened email expanded and the rest collapsed to sender and
 * snippet. It holds Remi's emails and the lead's replies; eAlerts that share
 * the conversation key are listed and opened on their own. An opened email
 * from Remi renders as it was sent.
 */
export const EmailThreadView: FC<ViewProps> = ({
  active: isOpen,
  contactId,
  contactName,
  contactEmail,
  conversationId,
  anchorMessageId,
  onContactClick,
  totalCount,
  emailKinds,
  children: frame,
}) => {
  const organization = useOrganizationStore((s) => s.organization);
  const timezone = organization?.timezone ?? 'UTC';
  const sender = orgEmailSender(organization);
  const headerName = contactName && contactName !== 'unknown' ? contactName : null;

  const thread = useEmailThreadGQL({
    conversationId,
    anchorMessageId,
    enabled: isOpen,
    emailKinds,
  });

  const conversation = thread.messages;
  // The opened email (or the latest one) starts expanded; others expand on click.
  const focusId = anchorMessageId || conversation[conversation.length - 1]?.messageId || null;
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const resetKey = `${isOpen}|${conversationId ?? ''}|${anchorMessageId ?? ''}`;
  const [trackedKey, setTrackedKey] = useState(resetKey);
  if (trackedKey !== resetKey) {
    setTrackedKey(resetKey);
    setToggled({});
  }
  const isExpanded = (id: string) => toggled[id] ?? id === focusId;

  // Bring the opened email into view once the thread has rendered.
  const listRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<string | null>(null);
  const hasMessages = thread.messages.length > 0;
  useEffect(() => {
    // A closed modal forgets the scroll, so reopening the same email brings it back into view.
    if (!isOpen) {
      scrolledFor.current = null;
      return;
    }
    if (!hasMessages || !focusId || scrolledFor.current === resetKey) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-msg-id="${CSS.escape(focusId)}"]`
    );
    if (!el) return;
    el.scrollIntoView({ block: 'start' });
    scrolledFor.current = resetKey;
  }, [isOpen, hasMessages, focusId, resetKey]);

  // Earlier and later emails load at the list's edges, as in the SMS thread.
  const { loadOlder, loadNewer } = useThreadEdgePaging({
    scrollRef: listRef,
    active: isOpen && hasMessages && !thread.loading,
    firstItemId: conversation[0]?.messageId ?? null,
    hasOlder: thread.hasOlder,
    isLoadingOlder: thread.isLoadingOlder,
    onLoadOlder: thread.loadOlder,
    hasNewer: thread.hasNewer,
    isLoadingNewer: thread.isLoadingNewer,
    onLoadNewer: thread.loadNewer,
  });
  const messageIds = useMemo(() => conversation.map((m) => m.messageId), [conversation]);
  const freshIds = useNewItemsHighlight(messageIds, resetKey);

  const leadEmail = (m: EmailThreadMessage) =>
    emailLeadAddress(m.direction, m.fromAddress, m.toAddress) || contactEmail || '';
  const lead = (m: EmailThreadMessage): Party => ({
    name: headerName || leadEmail(m) || 'Lead',
    email: leadEmail(m),
  });
  const remi: Party = { name: sender.name, email: sender.email };

  const subject =
    conversation.find((m) => m.subject)?.subject?.replace(/^\s*((re|fwd?)\s*:\s*)+/i, '') ||
    '(no subject)';
  const count = totalCount || thread.total || thread.messages.length;
  const headerEmail = contactEmail || (conversation[0] ? leadEmail(conversation[0]) : '') || null;

  return frame(
    <>
      <ContactHeader
        name={headerName}
        subtitle={[headerEmail, count ? `${count} ${count === 1 ? 'email' : 'emails'}` : null]
          .filter(Boolean)
          .join(' · ')}
        contactId={contactId}
        onContactClick={onContactClick}
        fallback={headerEmail ?? 'Email conversation'}
      />
    </>,
    <>
      {thread.loading ? (
        <div className="flex flex-col animate-pulse" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-start gap-3 py-4 border-b border-foreground/10">
              <div className="w-8 h-8 rounded-full bg-foreground/10" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 rounded bg-foreground/10" />
                <div className="h-3 w-2/3 rounded bg-foreground/10" />
              </div>
            </div>
          ))}
        </div>
      ) : thread.error && !hasMessages ? (
        <div className="flex justify-center py-8 text-sm text-muted-foreground">
          Couldn't load this email conversation.
        </div>
      ) : !hasMessages ? (
        <div className="flex justify-center py-8 text-sm text-muted-foreground">
          No emails in this conversation.
        </div>
      ) : (
        <>
          {/* Outside the scrolling list, so the subject stays in view while paging. */}
          <div className="mr-3 px-3 pt-1 pb-3.5 border-b border-foreground/10 text-[16px] font-medium text-foreground truncate flex-shrink-0">
            <span className="text-muted-foreground font-normal">Subject:</span> {subject}
          </div>
          <div
            ref={listRef}
            style={{ overflowAnchor: 'none' }}
            className="max-h-[calc(100vh-266px)] overflow-y-auto scrollbar-custom pr-3"
          >
            {thread.hasOlder && (
              <ThreadPagerPill
                direction="older"
                loading={thread.isLoadingOlder}
                onClick={loadOlder}
                label="Load older emails"
              />
            )}
            {thread.messages.map((m) => {
              const inbound = (m.direction || '').toLowerCase() === 'inbound';
              return (
                <EmailCard
                  key={m.messageId}
                  email={m}
                  from={inbound ? lead(m) : remi}
                  to={inbound ? remi : lead(m)}
                  expanded={isExpanded(m.messageId)}
                  highlighted={!!anchorMessageId && m.messageId === anchorMessageId}
                  fresh={freshIds.has(m.messageId)}
                  timezone={timezone}
                  onToggle={() =>
                    setToggled((t) => ({ ...t, [m.messageId]: !isExpanded(m.messageId) }))
                  }
                />
              );
            })}
            {thread.hasNewer && (
              <ThreadPagerPill
                direction="newer"
                loading={thread.isLoadingNewer}
                onClick={loadNewer}
                label="Load newer emails"
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export const EmailThreadModal: FC<Props> = ({ isOpen, onClose, ...rest }) => (
  <EmailThreadView active={isOpen} {...rest}>
    {(header, body) => (
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>{header}</Modal.Header>
        <Modal.Body className="flex-1 flex flex-col min-h-0 p-0">{body}</Modal.Body>
      </Modal>
    )}
  </EmailThreadView>
);

export default EmailThreadModal;
